import { useEffect, useState } from 'react';
import { FlowyFoundation } from '../../flowy';
import { RootGizmo } from '../../gizmos';
import {
  IGizmo,
  IPlatformCallbacks,
  IRootGizmoData,
  TOnSubmit,
} from '../../types';
import useFlowyGlobalContext from './use-flowy-global-context';

interface IUseFlowyFoundationParams {
  config?: IGizmo;
  parentRootGizmoFid?: string;
  data?: IRootGizmoData;
  onSubmit?: TOnSubmit;
  platformCallbacks?: IPlatformCallbacks;
  params?: any;
}

/**
 * Custom hook that will be used by the FlowyFoundation react component
 *
 * @param {IGizmo} config Gizmo's configuration
 * @param {string} parentRootGizmoFid parent root gizmo's fid (in case it's an annex form)
 * @param {IRootGizmoData} data root gizmo data
 * @param {TOnSubmit} onSubmit callback that can be provided by the platform layer, if it's
 *  provided it will be called when the form's validations are correct
 *
 * @returns custom hook
 */
const useFlowyFoundation = ({
  config,
  parentRootGizmoFid,
  data,
  onSubmit,
  platformCallbacks,
  params,
}: IUseFlowyFoundationParams) => {
  const flowyGlobal = useFlowyGlobalContext();
  const [flowyFoundation, setFlowyFoundation] = useState<
    FlowyFoundation | undefined
  >();
  const [rootGizmo, setRootGizmo] = useState<RootGizmo | undefined>();

  useEffect(() => {
    const apiUrl = flowyGlobal?.getApiUrl();
    const platformInterface = flowyGlobal?.getPlatformInterface();

    if (!params) {
      params = {};
    }

    const f = new FlowyFoundation({
      flowyGlobal,
      config,
      parentRootGizmoFid,
      data,
      onSubmit,
      platformCallbacks,
      apiUrl,
      platformInterface,
      params,
    });
    f.init().then((ff: FlowyFoundation) => {
      setFlowyFoundation(ff);
      setRootGizmo(ff.getRootGizmo());
    });

    return () => {
      f.end();
    };
  }, []);

  return {
    flowyFoundation,
    rootGizmo,
  };
};

export default useFlowyFoundation;
